import { handleActions } from 'redux-actions'
import updeep from 'updeep'
import {
  initializeSession,
  initializeSessionFail,
  initializeSessionSuccess,
  loginSuccess,
  logoutSuccess,
  saveLastUrl,
  signupFail,
  signupSuccess,
  updateUserInformationSuccess,
  setDeviceType,
  listUsers,
  listUsersSuccess,
  listUsersFail,
  confirmUserEmailSuccess,
  updateUserSuccess,
  getCurrentUser,
  getCurrentUserSuccess,
  getCurrentUserFail,
  signupOnApartmentConfirmationSuccess,
  getUserFail,
  getUser,
  getUserSuccess,
  sendPasswordResetCodeSuccess,
  loginFail,
  setWindowWidth
} from '../actions/users'
import { UserAttributes } from '../services/auth/types'
import { UsersState, UserIdIndex } from '../types/states'
import { CognitoUserData, User } from '../types/users'
import { Action } from './types'

const initialState: UsersState = {
  sessionInitializationAttempted: false,
  isLoadingSession: false,
  notConfirmedEmail: null,
  cognitoData: {
    isAuthenticated: false,
    isConfirmingEmail: false,
    attributes: {}
  },
  /** indexed by user id */
  users: {},
  areUsersLoading: false,
  device: {},
  isPasswordResetCodeSent: false
}

const users = handleActions<UsersState, any>(
  {
    [setDeviceType]: (state, { payload: isMobile }: Action<boolean>) => ({
      ...state,
      device: {
        ...state.device,
        isMobile
      }
    }),
    [setWindowWidth]: (state, { payload: windowWidth }: Action<string>) => ({
      ...state,
      device: {
        ...state.device,
        windowWidth
      }
    }),
    [initializeSession]: (state) => ({
      ...state,
      isLoadingSession: true
    }),
    [initializeSessionFail]: (state) => ({
      ...state,
      isLoadingSession: false,
      sessionInitializationAttempted: true
    }),
    [initializeSessionSuccess]: (
      state,
      { payload: cognitoData }: Action<CognitoUserData>
    ) => ({
      ...state,
      isLoadingSession: false,
      sessionInitializationAttempted: true,
      cognitoData: {
        ...cognitoData,
        isAuthenticated: true
      }
    }),
    [saveLastUrl]: (state, { payload: lastUrl }: Action<string>) => ({
      ...state,
      lastUrl
    }),
    [signupFail]: (state, { payload }: Action) =>
      updeep.updateIn('auth.error', payload.error, state),
    [signupSuccess]: (state) => ({
      ...state,
      cognitoData: {
        ...state.cognitoData,
        isConfirmingEmail: true
      }
    }),
    [loginSuccess]: (state) => ({
      ...state,
      cognitoData: {
        ...state.cognitoData,
        isAuthenticated: true
      }
    }),
    [loginFail]: (
      state,
      { payload: notConfirmedEmail }: Action<string | null>
    ) => ({
      ...state,
      notConfirmedEmail
    }),
    [logoutSuccess]: (state) => ({
      ...state,
      cognitoData: {
        ...state.cognitoData,
        isAuthenticated: false
      }
    }),
    [getUser]: (state) => ({
      ...state,
      areUsersLoading: true
    }),
    [getUserFail]: (state) => ({
      ...state,
      areUsersLoading: false
    }),
    [getUserSuccess]: (state, { payload: user }: Action<User>) => ({
      ...state,
      areUserLoading: false,
      users: {
        ...state.users,
        [user.userId]: user
      }
    }),
    [updateUserInformationSuccess]: (
      state,
      { payload: updatedUserAttributes }: Action<UserAttributes>
    ) => ({
      ...state,
      cognitoData: {
        ...state.cognitoData,
        attributes: {
          ...state.cognitoData.attributes,
          ...updatedUserAttributes
        }
      }
    }),
    [updateUserSuccess]: (state, { payload: updatedUser }: Action<User>) => ({
      ...state,
      users: {
        ...state.users,
        [updatedUser.userId]: updatedUser
      }
    }),
    [getCurrentUser]: (state) => ({
      ...state,
      areUsersLoading: true
    }),
    [getCurrentUserSuccess]: (state, { payload: user }: Action<User>) => ({
      ...state,
      users: {
        ...state.users,
        [user.userId]: user
      },
      areUsersLoading: false
    }),
    [getCurrentUserFail]: (state) => ({
      ...state,
      areUsersLoading: false
    }),
    [listUsers]: (state) => ({
      ...state,
      areUsersLoading: true
    }),
    [listUsersSuccess]: (state, { payload: users }: Action<User[]>) => {
      const indexedUsers = users.reduce((usersAcc: UserIdIndex, user) => {
        usersAcc[user.userId] = {
          ...user
        }
        return usersAcc
      }, {})
      return {
        ...state,
        users: indexedUsers || {},
        areUsersLoading: false
      }
    },
    [listUsersFail]: (state) => ({
      ...state,
      areUsersLoading: false
    }),
    [confirmUserEmailSuccess]: (state, { payload: userId }: Action<string>) => ({
      ...state,
      users: {
        ...state.users,
        [userId]: {
          ...state.users[userId],
          emailUnconfirmed: false
        }
      }
    }),
    [signupOnApartmentConfirmationSuccess]: (
      state,
      { payload: userId }: Action<string>
    ) => ({
      ...state,
      cognitoData: {
        ...state.cognitoData,
        sub: userId
      }
    }),
    [sendPasswordResetCodeSuccess]: (state) => ({
      ...state,
      isPasswordResetCodeSent: true
    })
  },
  initialState
)

export default users
